import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// SpacePage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home, Users, X } from 'lucide-react';
import { fetchEspacios, Espacio } from '../../../services/espacioService';
import AddEspacioModal from '../../../components/Spaces/AddEspacioModal';
import EditEspacioModal from '../../../components/Spaces/EditEspacioModal';
import DeleteEspacioModal from '../../../components/Spaces/DeleteEspacioModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.sp-page { font-family:'DM Sans',sans-serif; }
@keyframes spFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.sp-fadeup   { animation:spFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.sp-fadeup-1 { animation:spFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.sp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.sp-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32);}
.sp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:var(--ad-surface, #fff);border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151);border-radius:11px;
    padding:9px 14px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.sp-btn-secondary:hover{background:var(--ad-soft, #f8fafc);border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent);color:var(--ad-link, #2854c5);}
.sp-table-row{transition:background .15s;}
.sp-table-row:hover{background:var(--ad-soft, #fafcff);}
.sp-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.sp-img{width:44px;height:44px;border-radius:10px;object-fit:cover;cursor:pointer;border:1.5px solid var(--ad-line, #f1f5f9);transition:transform .2s;}
.sp-img:hover{transform:scale(1.08);}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const SpacePage: React.FC = () => {
    const [espacios, setEspacios]           = useState<Espacio[]>([]);
    const [showModal, setShowModal]         = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEspacio, setSelectedEspacio] = useState<Espacio|null>(null);
    const [currentPage, setCurrentPage]     = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('espacio', userLevelId);
    const [previewImg, setPreviewImg]       = useState<string|null>(null);

    const loadEspacios = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchEspacios();
                setEspacios(data.sort((a,b)=>a.id-b.id));
            } else setEspacios([]);
        } catch { setEspacios([]); }
    }, [perm]);

    useEffect(() => { loadEspacios(); }, [loadEspacios]);

    const totalPages       = Math.ceil(espacios.length / itemsPerPage);
    const displayedEspacios = espacios.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return <Loading text="Cargando espacios..." />;

    return <><LumaResourceWorkspace title="Espacios" description="Lista completa de espacios" group="Servicios" rows={espacios} columns={[{key:'nombre',label:'Nombre'},{key:'cantidad_personas',label:'Capacidad'},{key:'sucursal_nombre',label:'Sucursal'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowModal(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedEspacio(r);setShowEditModal(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedEspacio(r);setShowDeleteModal(true);}:undefined} history="/spaces/history"/><AddEspacioModal isOpen={showModal} onClose={()=>setShowModal(false)} onEspacioAdded={loadEspacios}/><EditEspacioModal isOpen={showEditModal} onClose={()=>setShowEditModal(false)} onEspacioUpdated={loadEspacios} espacio={selectedEspacio}/><DeleteEspacioModal isOpen={showDeleteModal} onClose={()=>setShowDeleteModal(false)} espacio={selectedEspacio} onDeleted={loadEspacios}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/></>;
};

export default SpacePage;
